import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

import { API_URL } from '@/config/api';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { token, user } = useAuth();
  const [theme, setThemeState] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('gc3h_theme');
    if (saved) return saved;
    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
    localStorage.setItem('gc3h_theme', theme);
  }, [theme]);

  // Sync with user preferences when logged in
  useEffect(() => {
    if (token && user) {
      fetchUserPreferences();
    }
  }, [token, user]);

  const fetchUserPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const prefs = await response.json();
        if (prefs.theme) {
          setThemeState(prefs.theme);
        }
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const setTheme = async (newTheme) => {
    setThemeState(newTheme);
    
    // Save to backend if logged in
    if (token) {
      try {
        await fetch(`${API_URL}/api/user/preferences`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({ theme: newTheme })
        });
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
